import {
  API_PREFIX,
  API_PATHS,
  type ApiErrorBody,
  type AuthSession,
} from '@uroute/shared';

/* ------------------------------- errors ---------------------------------- */

export class ApiError extends Error {
  readonly code: string;
  readonly status: number;
  readonly details?: unknown;
  readonly correlationId?: string;

  constructor(status: number, body: ApiErrorBody) {
    super(body.message);
    this.name = 'ApiError';
    this.status = status;
    this.code = body.code;
    this.details = body.details;
    this.correlationId = body.correlationId;
  }
}

/** The device could not reach the server (airplane mode, DNS, server down). */
export class NetworkError extends Error {
  readonly code = 'NETWORK_ERROR';
  readonly status = 0;
  constructor(message = "Can't reach UR-ROUTE. Check your connection and try again.") {
    super(message);
    this.name = 'NetworkError';
  }
}

/** Refresh token missing/revoked ??? the auth layer must sign the driver out. */
export class SessionExpiredError extends Error {
  readonly code = 'SESSION_EXPIRED';
  constructor(message = 'Your session has expired. Please sign in again.') {
    super(message);
    this.name = 'SessionExpiredError';
  }
}

export function isNetworkError(err: unknown): err is NetworkError {
  return err instanceof NetworkError || (err instanceof Error && err.name === 'TypeError');
}

/* ----------------------------- token storage ----------------------------- */

const K = {
  access: 'uroute.driver.accessToken',
  refresh: 'uroute.driver.refreshToken',
  stay: 'uroute.driver.staySignedIn',
  expires: 'uroute.driver.expiresAt',
} as const;

export interface TokenPair {
  access: string;
  refresh: string;
  expiresAt: number;
}

/**
 * "Stay signed in" keeps tokens in localStorage (survives the browser
 * closing); otherwise they live in sessionStorage (tab session only).
 * Reads always check both so a preference change never strands a session.
 */
export const tokenStore = {
  access: (): string | null => localStorage.getItem(K.access) ?? sessionStorage.getItem(K.access),
  refresh: (): string | null =>
    localStorage.getItem(K.refresh) ?? sessionStorage.getItem(K.refresh),
  expiresAt: (): number | null => {
    const raw = localStorage.getItem(K.expires) ?? sessionStorage.getItem(K.expires);
    const n = raw ? Number(raw) : NaN;
    return Number.isFinite(n) ? n : null;
  },
  staySignedIn: (): boolean => localStorage.getItem(K.stay) !== '0',
  set: (tokens: TokenPair, stay: boolean): void => {
    localStorage.removeItem(K.access);
    localStorage.removeItem(K.refresh);
    localStorage.removeItem(K.expires);
    sessionStorage.removeItem(K.access);
    sessionStorage.removeItem(K.refresh);
    sessionStorage.removeItem(K.expires);
    const store = stay ? localStorage : sessionStorage;
    store.setItem(K.access, tokens.access);
    store.setItem(K.refresh, tokens.refresh);
    store.setItem(K.expires, String(tokens.expiresAt));
    localStorage.setItem(K.stay, stay ? '1' : '0');
  },
  clear: (): void => {
    localStorage.removeItem(K.access);
    localStorage.removeItem(K.refresh);
    localStorage.removeItem(K.expires);
    sessionStorage.removeItem(K.access);
    sessionStorage.removeItem(K.refresh);
    sessionStorage.removeItem(K.expires);
  },
};

let sessionExpiredHandler: (() => void) | null = null;
export function setSessionExpiredHandler(fn: (() => void) | null): void {
  sessionExpiredHandler = fn;
}

/* -------------------------------- refresh -------------------------------- */

let refreshInFlight: Promise<boolean> | null = null;

async function performRefresh(): Promise<boolean> {
  const refreshToken = tokenStore.refresh();
  if (!refreshToken) return false;
  try {
    const res = await fetch(API_PATHS.auth.refresh, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken }),
    });
    if (!res.ok) return false;
    const session = (await res.json()) as AuthSession;
    tokenStore.set(
      {
        access: session.accessToken,
        refresh: session.refreshToken,
        expiresAt: Date.now() + session.expiresIn * 1000,
      },
      tokenStore.staySignedIn(),
    );
    return true;
  } catch {
    return false;
  }
}

/** Single-flight refresh so parallel 401s only rotate the token once. */
export function refreshSession(): Promise<boolean> {
  refreshInFlight ??= performRefresh().finally(() => {
    refreshInFlight = null;
  });
  return refreshInFlight;
}

/** Refresh a minute before expiry so normal requests never see a 401. */
async function ensureFresh(): Promise<void> {
  const expiresAt = tokenStore.expiresAt();
  if (expiresAt === null) return;
  if (expiresAt - Date.now() > 60_000) return;
  const ok = await refreshSession();
  if (!ok && tokenStore.access() && !tokenStore.refresh()) {
    sessionExpiredHandler?.();
  }
}

/* -------------------------------- requests -------------------------------- */

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
  auth?: boolean;
  signal?: AbortSignal;
}

function buildUrl(path: string): string {
  return path.startsWith('/') ? path : `${API_PREFIX}/${path}`;
}

function authHeaders(): Headers {
  const headers = new Headers();
  const token = tokenStore.access();
  if (token) headers.set('Authorization', `Bearer ${token}`);
  return headers;
}

async function parseBody(res: Response): Promise<unknown> {
  if (res.status === 204) return null;
  const text = await res.text();
  if (!text) return null;
  try {
    return JSON.parse(text) as unknown;
  } catch {
    return null;
  }
}

function toApiError(res: Response, parsed: unknown): ApiError | NetworkError {
  const envelope = (parsed as { error?: ApiErrorBody } | null)?.error;
  if (envelope) return new ApiError(res.status, envelope);
  const code: ApiErrorBody['code'] =
    res.status === 501
      ? 'NOT_IMPLEMENTED'
      : res.status >= 500
        ? 'INTERNAL_ERROR'
        : res.status === 429
          ? 'RATE_LIMITED'
          : res.status === 413
            ? 'PAYLOAD_TOO_LARGE'
            : res.status === 403
              ? 'FORBIDDEN'
              : res.status === 404
                ? 'NOT_FOUND'
                : res.status === 400
                  ? 'VALIDATION_ERROR'
                  : 'INTERNAL_ERROR';
  return new ApiError(res.status, {
    code,
    message:
      res.status === 501
        ? 'This part of UR-ROUTE is not available yet.'
        : res.status >= 500
          ? 'UR-ROUTE is having trouble right now. Try again in a moment.'
          : `Request failed (${res.status})`,
  });
}

async function doFetch(url: string, opts: RequestOptions): Promise<Response> {
  const headers = authHeaders();
  if (opts.body !== undefined) headers.set('Content-Type', 'application/json');
  try {
    return await fetch(url, {
      method: opts.method ?? 'GET',
      headers,
      body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
      signal: opts.signal,
    });
  } catch (err) {
    if (err instanceof DOMException && err.name === 'AbortError') throw err;
    throw new NetworkError();
  }
}

export async function request<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const url = buildUrl(path);
  const isAuthCall = url.startsWith('/api/v1/auth/');
  const auth = opts.auth !== false;

  if (auth && !isAuthCall) await ensureFresh();

  let res = await doFetch(url, opts);

  if (res.status === 401 && auth && !isAuthCall) {
    const ok = await refreshSession();
    if (ok) {
      res = await doFetch(url, opts);
    } else {
      tokenStore.clear();
      sessionExpiredHandler?.();
      throw new SessionExpiredError();
    }
  }

  const parsed = await parseBody(res);
  if (!res.ok) throw toApiError(res, parsed);
  return parsed as T;
}

export const api = {
  get: <T>(path: string, opts: Omit<RequestOptions, 'body' | 'method'> = {}) =>
    request<T>(path, { ...opts, method: 'GET' }),
  post: <T>(path: string, body?: unknown) => request<T>(path, { method: 'POST', body }),
  patch: <T>(path: string, body?: unknown) => request<T>(path, { method: 'PATCH', body }),
  del: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
};

/* ------------------------- multipart with progress ------------------------ */

/** XHR upload so the driver sees real byte progress (fetch gives none). */
export function upload<T>(
  path: string,
  formData: FormData,
  onProgress?: (pct: number) => void,
): Promise<T> {
  const send = async (attempt: number): Promise<T> =>
    new Promise<T>((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('POST', buildUrl(path));
      const token = tokenStore.access();
      if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);
      xhr.responseType = 'text';
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable && onProgress) {
          onProgress(Math.round((e.loaded / e.total) * 100));
        }
      };
      xhr.onerror = () => reject(new NetworkError());
      xhr.ontimeout = () => reject(new NetworkError());
      xhr.onload = () => {
        let parsed: unknown;
        try {
          parsed = xhr.responseText ? (JSON.parse(xhr.responseText) as unknown) : null;
        } catch {
          parsed = null;
        }
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve(parsed as T);
          return;
        }
        if (xhr.status === 401 && attempt === 0) {
          void refreshSession().then((ok) => {
            if (ok) {
              send(attempt + 1).then(resolve, reject);
            } else {
              tokenStore.clear();
              sessionExpiredHandler?.();
              reject(new SessionExpiredError());
            }
          });
          return;
        }
        reject(toApiError({ status: xhr.status, statusText: xhr.statusText } as Response, parsed));
      };
      xhr.send(formData);
    });
  return send(0);
}
